import {
  arrayToObj,
  getPercentageInfo,
  getPercentageValue,
  transRegion,
} from '@/pages/opinion/utils';
import { mergeSameKeyword } from '@/pages/opinion/utils/keyword';

/**
 * 创建计算同比变化的函数
 * @param item    新的对象
 * @param oldItem 老的对象
 * @returns (key: string) => ({ [key: string]: number }) 返回一个传key就能计算同比对象的函数
 */
function calculateRatioFn(
  item: { [key: string]: number },
  oldItem: { [key: string]: number },
) {
  return (key: string) => {
    const newValue = item?.[key];
    const oldValue = oldItem?.[key];
    let value = !newValue ? 0 : 100;
    if (oldValue) {
      // 同比变化的计算公式： （新 - 旧）/ 旧,最终值为百分比，所以×100，然后4舍5入保留两位小数
      value = Number((((newValue - oldValue) / oldValue) * 100).toFixed(2));
    }
    return { [key + '_than']: value };
  };
}

/**
 * 创建计算变化的函数
 * @param item    原始对象
 * @param oldItem 总数的对象
 * @returns (key: string) => ({ [key: string]: number }) 返回一个传key就能计算同比对象的函数
 */
function calculatePercentageFn(
  item: { [key: string]: number },
  oldItem: { [key: string]: number },
) {
  return (key: string) => {
    const newValue = item?.[key];
    const oldValue = oldItem?.[key];
    return {
      [key + '_percentage']: getPercentageValue(newValue, oldValue) * 100,
    };
  };
}

/**
 * 批量计算同比变化的函数
 * @param list            需要计算同比变化的属性key
 * @param calculateRatio  计算同比函数的方法
 */
function batchCalculateRatio(
  list: Array<string>,
  calculateRatio: (key: string) => { [key: string]: number | string },
) {
  return list.reduce((prev, curr) => {
    return { ...prev, ...calculateRatio(curr) };
  }, {});
}

/**
 * 处理表格数据，并添加同比变化的内容
 * @param newList
 * @param oldList
 * @returns
 */
export function dealwithTableData(
  newList: { [x: string]: number; name?: any }[],
  oldList: any[],
  max?: any,
) {
  if (!newList || newList.length === 0) return [];
  let keys = [
    'mentions',
    'negative_mentions',
    'non_negative_ratio',
    'sentiment',
    'followers',
    'views',
  ];
  const oldMap = arrayToObj(oldList, 'name');
  return newList.map((item: { [x: string]: number; name?: any }) => {
    const key = item.name;
    const thanMap = batchCalculateRatio(
      keys,
      calculateRatioFn(item, oldMap?.[key]),
    );
    let ratioMap = {};
    if (max) {
      ratioMap = batchCalculateRatio(keys, calculatePercentageFn(item, max));
    }
    return {
      ...item,
      ...thanMap,
      ...ratioMap,
    };
  });
}

export function getSentimentValue(value: number) {
  // 向下取整，但是最后保留一位小数是为了添加.0  【3 -> 3.0】
  return (Math.floor(value * 10) / 10).toFixed(1);
}

export function dealwithKeyword(keywordList: Array<any>, language: string) {
  return mergeSameKeyword(keywordList, language);
}

export type HeroMapType = {
  [key: string]: { 'zh-CN': string; 'en-US': string; icon: string };
};
export function dealwithCondHero({ hero }: any): HeroMapType {
  const map: any = {};
  hero?.forEach(
    (item: { name_en: string | number; name_zh: string; icon: string }) => {
      map[item.name_en] = {
        'zh-CN': item.name_zh,
        'en-US': item.name_en,
        icon: item.icon,
      };
    },
  );
  return map;
}

// 转换名称
function translateName(item: string, type: string) {
  switch (type) {
    case 'country':
      return transRegion(item, 1)?.toLowerCase() === 'global'
        ? 'Unidentified'
        : transRegion(item, 1);
    case 'market':
      return transRegion(item, 1);
    case 'language':
      return transRegion(item, 3);
    default:
      return item;
  }
}

export function dealwithLeftInfoData(data: {
  language: Array<any>;
  country: Array<any>;
  channel: Array<any>;
}) {
  const result: any = { language: [], country: [], channel: [] };
  for (const key in result) {
    const dataElement = data[key];
    const total = dataElement?.reduce((result: any, pre: { mentions: any }) => {
      return result + pre.mentions;
    }, 0);

    result[key] = dataElement?.map(
      (item: { [x: string]: any; mentions: any }) => {
        const name = item[key];
        const trans = translateName(name, key);
        return {
          name: name,
          text: trans,
          progress: getPercentageInfo(item.mentions, total),
          value: item.mentions,
        };
      },
    );

    result[key]?.sort(
      (a: { value: number }, b: { value: number }) => b.value - a.value,
    );
  }

  return result;
}
